import { profilePath } from "@travily/routes";
import type FriendRowType from "@travily/types/models/ProfileFriendshipsRow";
import { Link } from "@ui/primitives/Link";
import { AccountAvatar } from "@/components/AccountAvatar/AccountAvatar";
import { BACKEND_STATUS_MAP, FriendshipButton } from "@/components/FriendshipButton/FriendshipButton";

/** Per-row friendship handlers, bound to the row's account by the modal. */
export interface FriendRowActions {
  onBecomeFriends: (friend: FriendRowType) => void;
  onAccept: (friend: FriendRowType) => void;
  onDecline: (friend: FriendRowType) => void;
  onUnfriend: (friend: FriendRowType) => void;
}

interface FriendRowProps extends FriendRowActions {
  friend: FriendRowType;
  /** Disables the action button while this row's mutation is in flight. */
  isProcessing?: boolean;
}

/**
 * One row of the friends-list modal: avatar + name/handle (linking to the
 * profile) and the viewer's FriendshipButton. The viewer's own row never shows a
 * button — you can't befriend yourself.
 */
export function FriendRow({ friend, isProcessing, onBecomeFriends, onAccept, onDecline, onUnfriend }: FriendRowProps) {
  const status = BACKEND_STATUS_MAP[friend.friendship_status] ?? "default";

  return (
    <div className="flex items-center gap-3 py-2">
      <Link
        className="flex min-w-0 flex-1 items-center gap-3 text-left no-underline outline-none focus-visible:rounded-xl focus-visible:ring-2 focus-visible:ring-neutral-400"
        href={profilePath(friend.handle)}
      >
        <AccountAvatar account={friend} className="shrink-0" shape="circle" size="40" />
        {/* Two 20px lines so the name/handle block matches the 40px avatar height. */}
        <div className="flex min-w-0 flex-col">
          <span className="truncate font-semibold text-base/5 text-neutral-900">{friend.display_name}</span>
          <span className="truncate text-neutral-400 text-sm/5">{`@${friend.handle}`}</span>
        </div>
      </Link>
      {!friend.is_self && (
        <FriendshipButton
          className="shrink-0"
          isDisabled={isProcessing || friend.friendship_status === "pending_sent"}
          menuPlacement="bottom end"
          onAcceptFriendship={() => onAccept(friend)}
          onDeclineFriendship={() => onDecline(friend)}
          onPress={() => onBecomeFriends(friend)}
          onUnfriend={() => onUnfriend(friend)}
          size="36"
          status={status}
        />
      )}
    </div>
  );
}
